// KAN-25485 — defensive auth-storage clearer for the desktop frontend.
//
// Mirrors `apps/ios/lib/clearWebAuthStorage.ts` byte-for-byte (modulo
// import paths) so a session leak on either bundle gets the same cleanup.
// Kept as separate files because the mobile-web bundle is a separate JS
// graph from the Vite desktop SPA and a cross-package import would mean
// shipping the RN side's `react-native-url-polyfill/auto` to web for no
// reason.
//
// What this exists to fix: `supabase.auth.signOut()` is supposed to remove
// auth tokens from whatever storage adapter the client was constructed
// with (localStorage on web). In practice, fresh-user E2E walkthroughs
// (KAN-25485) caught `sb-*-auth-token` rehydrating from IndexedDB on the
// next page load — Supabase JS v2 can fall back to `localforage` (which
// uses IDB) when localStorage is unavailable or quota-exhausted, and
// `localStorage.clear()` does NOT touch IDB. This helper sweeps every
// IDB database name that has historically held a Supabase auth token
// and deletes them.
//
// Safe to call:
//   * after signOut() (clears the residue the SDK missed)
//   * on /sign-up first render when no real session is in flight (force-
//     clears any leaked IDB before the signup form binds form values)
//
// No-op under SSR (no `window`) and no-op in environments without
// `indexedDB` (older browsers, restricted contexts).

const KNOWN_DB_NAMES = [
  // localforage default — the most common Supabase storage fallback.
  'localforage',
  // @react-native-async-storage/async-storage's IndexedDB backend
  // (used by certain versions on browsers without localStorage quota).
  // Bug Hunter PR #629 P3 fix: align with the mobile-web helper so a
  // desktop user who had a stale tab from the /m/ bundle gets the same
  // sweep. AsyncStorage IDB residue can survive a domain-wide
  // localStorage clear and rehydrate the desktop auth context.
  'RKStorage',
  'AsyncStorage',
  // Generic key-value-store fallback used by various supabase auth shims.
  'keyval-store',
  // Supabase-specific names that have been observed in the wild.
  'supabase-auth',
  'supabase-storage',
];

const SUPABASE_DB_PATTERNS: Array<(name: string) => boolean> = [
  (name) => name.toLowerCase().startsWith('sb-'),
  (name) => name.toLowerCase().includes('supabase'),
  (name) => name.toLowerCase().includes('localforage'),
  // Bug Hunter PR #629 P3 fix: same asyncstorage pattern as the mobile-web
  // helper. A user who hit /m/ on the same domain (mobile-web) can leave
  // AsyncStorage-prefixed IDB databases the desktop sweep would miss.
  (name) => name.toLowerCase().includes('asyncstorage'),
];

function deleteDatabaseAsync(name: string): Promise<void> {
  return new Promise<void>((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name);
      // Resolve on ANY terminal state — success, error, or blocked. `blocked`
      // happens when another tab still has the DB open; don't hang the
      // sign-out path waiting for that tab.
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

export async function clearWebAuthStorage(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (typeof indexedDB === 'undefined') return;

  // Sweep IndexedDB.
  const names = new Set<string>(KNOWN_DB_NAMES);
  try {
    const anyIdb = indexedDB as unknown as {
      databases?: () => Promise<Array<{ name?: string }>>;
    };
    if (typeof anyIdb.databases === 'function') {
      const dbs = await anyIdb.databases();
      for (const db of dbs) {
        const n = db?.name;
        if (!n) continue;
        if (SUPABASE_DB_PATTERNS.some((m) => m(n))) {
          names.add(n);
        }
      }
    }
  } catch {
    // Safari private mode / older browsers without indexedDB.databases() —
    // fall through to the hard-coded list.
  }
  await Promise.all(Array.from(names).map(deleteDatabaseAsync));

  // Sweep localStorage too — same patterns. The SupabaseAuthContext
  // `logout()` already does this for `sb-*-auth-token` but we duplicate
  // here so callers in other surfaces get the same guarantee.
  try {
    if (typeof window.localStorage !== 'undefined') {
      const toRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (!k) continue;
        if (k.startsWith('sb-') || k.toLowerCase().includes('supabase')) {
          toRemove.push(k);
        }
      }
      toRemove.forEach((k) => window.localStorage.removeItem(k));
    }
  } catch {
    /* private mode — non-fatal */
  }
}
